import {
  useShow,
  useGo,
  type HttpError,
} from "@refinedev/core";
import { Shield } from "@/components/ui/icon";
import type { Role } from "@aos/shared";

import { Button } from "@/components/ui/button";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  ShowPageSkeleton,
  FieldGrid,
  Field,
  RelationsBlock,
  AuditBlock,
  TechnicalId,
} from "@/components/inspector";
import { Markdown } from "@/components/app";
import { humanize } from "../projects/_form";
import { ProposedListPage } from "../_shared/ProposedModule";
import { RoleGrantMatrix } from "../admin/RoleGrantMatrix";

export function RoleShow() {
  const go = useGo();
  const { query } = useShow<Role, HttpError>({
    resource: "roles",
    queryOptions: { retry: false },
    errorNotification: false,
  });
  const { data, isLoading, isError } = query;
  const role = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !role) {
    return (
      <ProposedListPage
        title="Role"
        description="The RBAC roles assignable to users."
        icon={Shield}
        endpoint="/api/roles/:id"
        emptyTitle="Role detail not available"
        testId="role-proposed"
      >
        <Button variant="outline" onClick={() => go({ to: "/roles" })}>
          Back to roles
        </Button>
      </ProposedListPage>
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title={humanize(role.name)}
        subtitle="An RBAC role assignable to users."
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Role">{humanize(role.name)}</Field>
          <Field label="Key">
            <span className="font-mono text-xs">{role.name}</span>
          </Field>
          <Field label="Description" full>
            {role.description ? (
              <Markdown source={role.description} />
            ) : (
              <span className="text-muted-foreground">No description.</span>
            )}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Permissions">
        <RoleGrantMatrix roleName={role.name} />
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "Member users",
              emptyMessage:
                "Member users available once the user_roles join endpoint ships ([PROPOSED]).",
              items: [],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={role.id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="roles" entityId={role.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default RoleShow;
